import React, { useState, useCallback, useRef, useEffect, Suspense } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Panel,
} from '@xyflow/react';
import { nanoid } from 'nanoid';
import { useTheme } from './contexts/ThemeContext';

import Sidebar from './components/Sidebar';
import NodeConfigModal from './components/NodeConfigModal';
import ContextMenu from './components/ContextMenu';
import TopBar from './components/TopBar';
import TemplateManager from './components/TemplateManager';
import ExportImportModal from './components/ExportImportModal';
import LLMAssistPanel from './components/LLMAssistPanel';
import VariableManager from './components/VariableManager';
import PreviewPlayer from './components/PreviewPlayer';
import EmbedCodeGenerator from './components/EmbedCodeGenerator';
import AutomateFlowPanel from './components/AutomateFlowPanel';
import TestFlowModal from './components/TestFlowModal';
import { nodeTypes } from './components/nodes';
import { initialNodes, initialEdges } from './data/initialFlow';
import { VariableProvider } from './contexts/VariableContext';
import { useNotifications } from './components/NotificationSystem';
import websocketService from './services/websocket';
import FlowValidator from './utils/flowValidator';

import '@xyflow/react/dist/style.css';
// Compact 70% support
const useCompactScaling = () => {
  const [compact, setCompact] = React.useState(() => localStorage.getItem('compact-70') === '1');
  React.useEffect(() => { localStorage.setItem('compact-70', compact ? '1' : '0'); }, [compact]);
  return { compact, setCompact };
};


import Dashboard from './components/Dashboard';
import apiService from './services/api';

// Brook-Flows has no sign-in UI of its own — a signed-out visit redirects
// here, to 7h3.agency's own Clerk-gated /flow entry point, which
// authenticates via Clerk and hands back an already-authenticated session
// (see 7h3-Agency's worker/index.ts "/flow" route and flowProxy.ts's
// injectFlowAccessToken, which seeds the access_token this app reads below).
const SIGNED_OUT_REDIRECT_URL = 'https://7h3.agency/flow';

function App() {
  const { showSuccess, showError, showWarning } = useNotifications();

  // ── Auth ──
  const [currentUser, setCurrentUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('af_user') || 'null'); } catch { return null; }
  });
  const [authChecked, setAuthChecked] = useState(false);
  const [currentFlowId, setCurrentFlowId] = useState(null);
  const [currentFlowName, setCurrentFlowName] = useState('Untitled Flow');

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) { setAuthChecked(true); return; }
    // Verify token is still valid
    apiService.getMe().then(user => {
      setCurrentUser(user);
      localStorage.setItem('af_user', JSON.stringify(user));
    }).catch(() => {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('af_user');
      setCurrentUser(null);
    }).finally(() => setAuthChecked(true));
  }, []);

  useEffect(() => {
    if (authChecked && !currentUser) {
      window.location.href = SIGNED_OUT_REDIRECT_URL;
    }
  }, [authChecked, currentUser]);

  // Not currently wired to any UI. If it is, note that clearing
  // currentUser here will trigger the signed-out redirect effect above —
  // since auth is now fully federated through 7h3.agency's Clerk session,
  // that bounce will silently re-authenticate (not truly sign out) if the
  // 7h3.agency Clerk session is still active. A real sign-out needs to
  // end that session too, not just clear Brook-Flows' local token.
  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('af_user');
    setCurrentUser(null);
    setCurrentFlowId(null);
    setCurrentFlowName('Untitled Flow');
  };

  // App View State: 'dashboard' | 'builder'
  const [view, setView] = useState('dashboard');

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);

  // Dashboard handlers
  const handleQuickLoadProject = useCallback((project) => {
    const flow = project?.flow || (project?.flow_data
      ? (typeof project.flow_data === 'string' ? JSON.parse(project.flow_data) : project.flow_data)
      : null);
    if (flow?.nodes && flow?.edges) {
      setNodes(flow.nodes);
      setEdges(flow.edges);
      showSuccess(`Loaded project: ${project.name}`, 'Project Loaded');
    } else {
      showWarning('Project has no saved flow.', 'Project Empty');
    }
    setView('builder');
  }, [setNodes, setEdges, showSuccess, showWarning]);

  const handleQuickTestProject = useCallback((project) => {
    const flow = project?.flow || (project?.flow_data
      ? (typeof project.flow_data === 'string' ? JSON.parse(project.flow_data) : project.flow_data)
      : null);
    if (flow?.nodes && flow?.edges) {
      setNodes(flow.nodes);
      setEdges(flow.edges);
      setView('builder');
      setShowPreviewPlayer(true);
    } else {
      showWarning('Project has no saved flow to preview.', 'Project Empty');
    }
  }, [setNodes, setEdges, showWarning]);
  const [isTesting, setIsTesting] = useState(false);
  const [testFlowResults, setTestFlowResults] = useState(null);
  const [showTestFlowModal, setShowTestFlowModal] = useState(false);
  const [executionStatus, setExecutionStatus] = useState({});
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [showExportImport, setShowExportImport] = useState(false);
  const [showLLMAssist, setShowLLMAssist] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showVariableManager, setShowVariableManager] = useState(false);
  const [showPreviewPlayer, setShowPreviewPlayer] = useState(false);
  const [showEmbedGenerator, setShowEmbedGenerator] = useState(false);
  const [showAutomatePanel, setShowAutomatePanel] = useState(false);
  const [highlightedNodeId, setHighlightedNodeId] = useState(null);

  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  // Expose current flow for embed code generator
  useEffect(() => {
    window.getCurrentFlow = () => ({ nodes, edges });
    return () => { delete window.getCurrentFlow; };
  }, [nodes, edges]);

  // Initialize WebSocket connection (optional - gracefully handles backend being offline)
  useEffect(() => {
    // Try to connect, but don't show errors if backend is unavailable
    websocketService.connect().catch(() => {
      // Backend may not be running - this is expected in some scenarios
      // Features will use localStorage/fallbacks instead
    });

    // Listen for execution updates
    websocketService.on('execution_status', (data) => {
      console.log('Execution status:', data);
    });

    websocketService.on('node_status', (data) => {
      setExecutionStatus(prev => ({
        ...prev,
        [data.node_id]: data.status
      }));
    });

    // Listen for embed code generator event
    const handleEmbedEvent = () => setShowEmbedGenerator(true);
    window.addEventListener('ai-flow-embed', handleEmbedEvent);

    return () => {
      websocketService.disconnect();
      window.removeEventListener('ai-flow-embed', handleEmbedEvent);
    };
  }, []);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');
      if (typeof type === 'undefined' || !type) {
        return;
      }

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode = {
        id: nanoid(),
        type,
        position,
        data: {
          label: `${type} node`,
          config: {},
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, setNodes]
  );

  const onNodeContextMenu = useCallback(
    (event, node) => {
      event.preventDefault();
      setContextMenu({
        id: node.id,
        top: event.clientY,
        left: event.clientX,
      });
    },
    []
  );

  const onPaneClick = useCallback(() => {
    setContextMenu(null);
  }, []);

  const onNodeDoubleClick = useCallback((_event, node) => {
    setSelectedNode(node);
  }, []);

  const handleNodeUpdate = useCallback((nodeId, newData) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, ...newData } }
          : node
      )
    );
  }, [setNodes]);

  const handleNodeDelete = useCallback((nodeId) => {
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
    setContextMenu(null);
  }, [setNodes, setEdges]);

  const handleNodeDuplicate = useCallback((nodeId) => {
    const nodeToDuplicate = nodes.find((node) => node.id === nodeId);
    if (nodeToDuplicate) {
      const newNode = {
        ...nodeToDuplicate,
        id: nanoid(),
        position: {
          x: nodeToDuplicate.position.x + 50,
          y: nodeToDuplicate.position.y + 50,
        },
      };
      setNodes((nds) => nds.concat(newNode));
    }
    setContextMenu(null);
  }, [nodes, setNodes]);

  const testFlow = useCallback(() => {
    setIsTesting(true);
    try {
      const validator = new FlowValidator();
      const results = validator.validate({ nodes, edges });
      setTestFlowResults(results);
      setShowTestFlowModal(true);
    } finally {
      setIsTesting(false);
    }
  }, [nodes, edges]);

  // Shared by Dashboard's "open project" click and the ?flow=<id> deep-link
  // effect below — fetches one flow's full nodes/edges (the list endpoint
  // omits them) and switches straight to the builder with it loaded.
  const loadFlowById = useCallback(async (id, fallbackName) => {
    setView('builder');
    try {
      const full = await apiService.get(`/flows/${id}`);
      setNodes(Array.isArray(full.nodes) ? full.nodes : []);
      setEdges(Array.isArray(full.edges) ? full.edges : []);
      setCurrentFlowId(full.id);
      setCurrentFlowName(full.name || fallbackName || 'Untitled Flow');
      showSuccess(`Loaded: ${full.name || fallbackName || 'flow'}`, 'Flow Loaded');
    } catch {
      setCurrentFlowId(id);
      setCurrentFlowName(fallbackName || 'Untitled Flow');
      showError('Could not load flow nodes — try saving again.', 'Load Error');
    }
  }, [setNodes, setEdges, showSuccess, showError]);

  // 7h3-Agency's "Approve — Open in AI Flow" button links here as
  // /flow?h=<handoff-code>&flow=<id> — the handoff code is consumed
  // server-side (worker/index.ts's exact-"/flow" route) before this page is
  // ever served, but `flow` passes straight through in the URL since that
  // route serves this app's own HTML rather than redirecting. Loads once
  // signed in, then drops the param so a refresh doesn't reload it forever.
  useEffect(() => {
    if (!authChecked || !currentUser) return
    const params = new URLSearchParams(window.location.search)
    const flowId = params.get('flow')
    if (!flowId) return
    loadFlowById(flowId)
    params.delete('flow')
    params.delete('h')
    const nextSearch = params.toString()
    window.history.replaceState(null, '', `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`)
  }, [authChecked, currentUser, loadFlowById])

  const saveFlow = useCallback(async () => {
    const name = currentFlowName.trim() || 'Untitled Flow';
    const flowData = { nodes, edges, name, timestamp: new Date().toISOString() };
    localStorage.setItem('ai-flow-save', JSON.stringify(flowData));
    try {
      const result = await apiService.saveFlow(currentFlowId, name, nodes, edges);
      if (result?.id && !currentFlowId) setCurrentFlowId(result.id);
      showSuccess(`"${name}" saved to cloud`, 'Saved');
    } catch {
      showSuccess('Flow saved locally', 'Saved');
    }
  }, [nodes, edges, currentFlowId, currentFlowName, showSuccess]);

  const loadFlow = useCallback(() => {
    const savedFlow = localStorage.getItem('ai-flow-save');
    if (savedFlow) {
      try {
        const flowData = JSON.parse(savedFlow);
        setNodes(flowData.nodes || []);
        setEdges(flowData.edges || []);
        showSuccess('Flow loaded successfully!', 'Load Complete');
      } catch (error) {
        showError('Failed to parse saved flow data.', 'Load Error');
      }
    } else {
      showWarning('No saved flow found!', 'Load Warning');
    }
  }, [setNodes, setEdges, showSuccess, showError, showWarning]);

  const handleTemplateLoad = useCallback((templateNodes) => {
    const newNodes = templateNodes.map(node => ({
      ...node,
      id: nanoid(),
      position: {
        x: node.position.x + Math.random() * 100,
        y: node.position.y + Math.random() * 100,
      }
    }));
    setNodes(prev => [...prev, ...newNodes]);
  }, [setNodes]);

  const handleFlowImport = useCallback((importedFlow) => {
    setNodes(importedFlow.nodes || []);
    setEdges(importedFlow.edges || []);
  }, [setNodes, setEdges]);

  const handleLLMSuggestion = useCallback((suggestion) => {
    if (suggestion.type === 'add_node') {
      const newNode = {
        id: nanoid(),
        type: suggestion.nodeType,
        position: { x: 300, y: 300 },
        data: {
          label: suggestion.label || `${suggestion.nodeType} node`,
          config: {},
        },
      };
      setNodes(prev => [...prev, newNode]);
    }
    // Handle other suggestion types as needed
  }, [setNodes]);

  const { compact, setCompact } = useCompactScaling();
  const { themeStyle } = useTheme();

  // Show loading spinner while auth check is in flight
  if (!authChecked) {
    return (
      <div className="af-shell flex h-screen items-center justify-center" style={themeStyle}>
        <div className="af-text-muted" style={{ letterSpacing: '0.1em', fontSize: '0.8rem' }}>LOADING...</div>
      </div>
    );
  }

  // Gate on auth — the redirect itself is triggered by the effect above;
  // this just avoids rendering the builder UI while that navigation happens.
  if (!currentUser) {
    return (
      <div className="af-shell flex h-screen items-center justify-center" style={themeStyle}>
        <div className="af-text-muted" style={{ letterSpacing: '0.1em', fontSize: '0.8rem' }}>REDIRECTING TO SIGN IN...</div>
      </div>
    );
  }

  return (
    <div className="af-shell flex h-screen" style={themeStyle}>
      {/* Sidebar - Only visible in builder view */}
      {view === 'builder' && <Sidebar />}

      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar
          onTestFlow={testFlow}
          onSave={saveFlow}
          onLoad={loadFlow}
          onShowTemplates={() => setShowTemplateManager(true)}
          onShowExportImport={() => setShowExportImport(true)}
          onToggleLLMAssist={() => setShowLLMAssist(!showLLMAssist)}
          onToggleHeatmap={() => setShowHeatmap(!showHeatmap)}
          onShowVariables={() => setShowVariableManager(true)}
          onTogglePreview={() => setShowPreviewPlayer(true)}
          onShowEmbedCode={() => setShowEmbedGenerator(true)}
          onShowAutomate={() => setShowAutomatePanel(true)}
          isTesting={isTesting}
          showLLMAssist={showLLMAssist}
          showHeatmap={showHeatmap}
          showPreviewPlayer={showPreviewPlayer}
          compact75={compact}
          onToggleCompact={() => setCompact(v => !v)}
          view={view}
          onViewChange={setView}
          currentFlow={currentFlowName}
          onFlowChange={setCurrentFlowName}
        />

        <div className="flex flex-1 overflow-hidden relative">
          {view === 'dashboard' ? (
            <div className="w-full h-full overflow-auto" style={{ background: 'var(--bg)' }}>
              <Dashboard
                onQuickLoadProject={handleQuickLoadProject}
                onTestProject={handleQuickTestProject}
                onLoadFlow={(flow) => loadFlowById(flow.id, flow.name)}
                onNewFlow={() => {
                  setNodes([]);
                  setEdges([]);
                  setCurrentFlowId(null);
                  setCurrentFlowName('Untitled Flow');
                  setView('builder');
                }}
              />
            </div>
          ) : (
            /* Builder View */
            <div className={`flex-1 w-full h-full ${compact ? 'builder-scale-70 builder-wrapper' : ''}`} ref={reactFlowWrapper}>
              <Suspense fallback={
                <div className="flex items-center justify-center h-full" style={{ background: 'var(--bg)', color: 'var(--accent)' }}>
                  <div className="text-center" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem' }}>
                    <div style={{ border: '1px solid var(--accent)', padding: '0.5rem 1rem', marginBottom: '0.5rem', opacity: 0.7 }}>
                      INITIALISING FLOW ENGINE...
                    </div>
                  </div>
                </div>
              }>
                <ReactFlow
                  nodes={nodes.map((node) => ({
                    ...node,
                    className: `${executionStatus[node.id] ? `node-${executionStatus[node.id]}` : ''} ${showHeatmap ? 'heatmap-overlay' : ''
                      } ${highlightedNodeId === node.id ? 'node-highlighted' : ''}`,
                  }))}
                  edges={edges.map((edge) => ({
                    ...edge,
                    className: executionStatus[edge.source] === 'executing' ? 'executing' : '',
                  }))}
                  defaultEdgeOptions={{
                    type: 'smoothstep',
                    style: { stroke: 'color-mix(in srgb, var(--line) 55%, transparent)', strokeWidth: 1.5 },
                  }}

                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={onConnect}
                  onInit={setReactFlowInstance}
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                  onNodeContextMenu={onNodeContextMenu}
                  onNodeDoubleClick={onNodeDoubleClick}
                  onPaneClick={onPaneClick}
                  nodeTypes={nodeTypes}
                  fitView
                >
                  <Controls />
                  <MiniMap />
                  <Background variant="dots" gap={12} size={1} />

                  <Panel position="top-right">
                    <div style={{
                      fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem',
                      background: 'color-mix(in srgb, var(--panel) 92%, black 8%)',
                      border: '1px solid color-mix(in srgb, var(--line) 25%, transparent)',
                      color: 'var(--muted)', padding: '0.3em 0.6em', letterSpacing: '0.06em',
                    }}>
                      N:{nodes.length} E:{edges.length}
                      {isTesting && <span style={{ color: 'var(--accent)', marginLeft: '0.5em' }}>TESTING…</span>}
                    </div>
                  </Panel>

                  {showHeatmap && (
                    <Panel position="top-left">
                      <div style={{
                        fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem',
                        background: 'color-mix(in srgb, var(--panel) 92%, black 8%)',
                        border: '1px solid color-mix(in srgb, var(--line) 25%, transparent)',
                        color: 'var(--text)', padding: '0.5em 0.8em',
                      }}>
                        <div style={{ color: 'var(--accent-2)', marginBottom: '0.3em', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Heatmap</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4em', marginBottom: '0.2em' }}><div style={{ width: 8, height: 8, background: 'var(--accent)' }} /> Optimal</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4em', marginBottom: '0.2em' }}><div style={{ width: 8, height: 8, background: '#f59e0b' }} /> High Load</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4em' }}><div style={{ width: 8, height: 8, background: '#ff4e7a' }} /> Bottleneck</div>
                      </div>
                    </Panel>
                  )}
                </ReactFlow>
              </Suspense>
            </div>
          )}

          {view === 'builder' && showLLMAssist && (
            <LLMAssistPanel
              flow={{ nodes, edges }}
              onClose={() => setShowLLMAssist(false)}
              onSuggestion={handleLLMSuggestion}
            />
          )}
        </div>
      </div>

      {selectedNode && (
        <NodeConfigModal
          node={selectedNode}
          onClose={() => setSelectedNode(null)}
          onUpdate={handleNodeUpdate}
        />
      )}

      {contextMenu && (
        <ContextMenu
          {...contextMenu}
          onClose={() => setContextMenu(null)}
          onDelete={handleNodeDelete}
          onDuplicate={handleNodeDuplicate}
          onEdit={(nodeId) => {
            const node = nodes.find((n) => n.id === nodeId);
            setSelectedNode(node);
            setContextMenu(null);
          }}
        />
      )}

      {showTemplateManager && (
        <TemplateManager
          nodes={nodes}
          onLoadTemplate={handleTemplateLoad}
          onClose={() => setShowTemplateManager(false)}
        />
      )}

      {showExportImport && (
        <ExportImportModal
          flow={{ nodes, edges }}
          onImport={handleFlowImport}
          onClose={() => setShowExportImport(false)}
        />
      )}

      {showVariableManager && (
        <VariableManager
          onClose={() => setShowVariableManager(false)}
        />
      )}

      {showEmbedGenerator && (
        <EmbedCodeGenerator
          isOpen={showEmbedGenerator}
          onClose={() => setShowEmbedGenerator(false)}
          flowId={currentFlowId}
          flowName="AI Flow"
          flowNodes={nodes}
        />
      )}

      <AutomateFlowPanel
        isOpen={showAutomatePanel}
        onClose={() => setShowAutomatePanel(false)}
        flowId={currentFlowId}
      />

      {showPreviewPlayer && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <PreviewPlayer
            flow={{ nodes, edges }}
            onClose={() => setShowPreviewPlayer(false)}
            onNodeHighlight={setHighlightedNodeId}
          />
        </div>
      )}

      <TestFlowModal
        isOpen={showTestFlowModal}
        onClose={() => setShowTestFlowModal(false)}
        results={testFlowResults}
      />
    </div>
  );
}

const AppWithProviders = () => {
  return (
    <VariableProvider>
      <App />
    </VariableProvider>
  );
};

export default AppWithProviders;
